import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ReactText } from 'react';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { ContractCostRequest, ContractCostResponse, ContractCostsClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';
import { CostsDataModel } from 'src/views/contract/vesselContract/Import/DataTypes';

export interface ContractCost extends ContractCostResponse { }

export interface State {
  isLoading: boolean;
  error: boolean;
  contractCostList: ContractCost[];
}

const initialState: State = {
  isLoading: true,
  error: false,
  contractCostList: [],
};

const contractCostSlice = createSlice({
  name: 'contractCost',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
    },
    hasError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    getContractCostListSuccess: (state, action: PayloadAction<ContractCost[]>) => {
      state.contractCostList = action.payload;
      state.isLoading = false;
    },
    addNewContractCostSuccess: (state, action: PayloadAction<ContractCost>) => {
      const newItem = action.payload;
      state.contractCostList = [newItem, ...state.contractCostList];
      toast.success(stringResources.REDUX_SLICE.CREATE_SUCCESS);
      state.isLoading = false;
    },
    updateContractCostSuccess: (state, action: PayloadAction<ContractCost>) => {
      const updatedItem = action.payload;
      const index = state.contractCostList.findIndex(item => item.contractCostId === updatedItem.contractCostId);
      state.contractCostList = [...state.contractCostList.slice(0, index), updatedItem, ...state.contractCostList.slice(index + 1)];
      toast.success(stringResources.REDUX_SLICE.UPDATE_SUCCESS);
      state.isLoading = false;
    },
    deleteContractCostSuccess: (state, action: PayloadAction<number>) => {
      const contractCostId = action.payload;
      state.contractCostList = state.contractCostList.filter(item => item.contractCostId !== contractCostId);
      state.isLoading = false;
    },
  },
});

// Reducer
export default contractCostSlice.reducer

// Actions
export const {
  startLoading,
  hasError,
} = contractCostSlice.actions;

// for useSelector
export const selectContractCost = (state: RootState) => state.contractCost as State;

// ----------------------------------------------------------------------

export function getContractCostList(vesselContractId: number) {
  return async (dispatch, getState) => {
    dispatch(contractCostSlice.actions.startLoading());
    try {
      const client = new ContractCostsClient(undefined, axios);
      const response = await client.getByVesselContractId(vesselContractId);
      dispatch(contractCostSlice.actions.getContractCostListSuccess(response));
      return response;
    } catch (error) {
      dispatch(contractCostSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function createContractCost(contractCost: ContractCost) {
  return async (dispatch, getState) => {
    dispatch(contractCostSlice.actions.startLoading());
    try {
      const { contractCostId, ...payload } = contractCost;
      const request = payload as ContractCostRequest;
      const client = new ContractCostsClient(undefined, axios);
      const response = await client.create(request);
      dispatch(contractCostSlice.actions.addNewContractCostSuccess(response));
    } catch (error) {
      dispatch(contractCostSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function createContractCostBatch(vesselContractId: number, CostsData: CostsDataModel[],
  toastId: ReactText, currentProgress: number, totalProgress: number) {
  return async (dispatch, getState) => {
    try {
      const client = new ContractCostsClient(undefined, axios);
      for (const d of CostsData) {
        const request = {
          vesselContractId: vesselContractId,
          description: d.Description,
          unitPrice: d.UnitPrice
        } as ContractCostRequest;
        await client.create(request);
        currentProgress += 1;
        toast.update(toastId, { render: `${stringResources.IMPORT.COSTS_IMPORTING} (${currentProgress}/${totalProgress})`, progress: currentProgress / totalProgress });
      }
    } catch (error) {
      handleError(error);
    }
  };
}

export function updateContractCost(contractCost: ContractCost) {
  return async (dispatch, getState) => {
    dispatch(contractCostSlice.actions.startLoading());
    try {
      const { contractCostId, ...payload } = contractCost;
      const request = payload as ContractCostRequest;
      const client = new ContractCostsClient(undefined, axios);
      const response = await client.edit(contractCostId, request);
      dispatch(contractCostSlice.actions.updateContractCostSuccess(response));
    } catch (error) {
      dispatch(contractCostSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function deleteContractCost(contractCostId: number, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(contractCostSlice.actions.startLoading());
    try {
      const client = new ContractCostsClient(undefined, axios);
      await client.delete(contractCostId);
      dispatch(contractCostSlice.actions.deleteContractCostSuccess(contractCostId));
      if (showNotification) {
        toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
      }
      return true;
    } catch (error) {
      dispatch(contractCostSlice.actions.hasError(true));
      handleError(error);
      return false;
    }
  };
}
